import React, { useState, useEffect } from 'react';
import { FiGlobe, FiCamera, FiImage, FiUploadCloud, FiX, FiCalendar, FiClock } from 'react-icons/fi';
import { plantAnalysisService } from '../services/plantAnalysisService';
import { compressImage } from '../utils/imageUtils';

interface UploadPageProps {
  setCurrentPage: (page: 'home' | 'upload' | 'collection' | 'about' | 'resources' | 'loading' | 'chat') => void;
  startAnalysis: (file: File, region: string, imageDataUrl?: string) => void;
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
}

type InputMode = 'camera' | 'upload';

function UploadPage({ setCurrentPage, startAnalysis, selectedRegion, setSelectedRegion }: UploadPageProps) {
  // Fix for TS2786: Cast icons to any to resolve ReactNode vs Element type mismatch
  const IconGlobe = FiGlobe as any;
  const IconCamera = FiCamera as any;
  const IconImage = FiImage as any;
  const IconUploadCloud = FiUploadCloud as any;
  const IconX = FiX as any;
  const IconCalendar = FiCalendar as any;
  const IconClock = FiClock as any;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<InputMode>('camera');
  const [timeOfYear, setTimeOfYear] = useState<string>('');
  const [timeOfDay, setTimeOfDay] = useState<string>('');

  const texasRegions = [
    'North Texas', 'Central Texas', 'East Texas', 
    'West Texas', 'South Texas', 'Gulf Coast', 'Panhandle'
  ];

  useEffect(() => {
    if (!selectedRegion) setSelectedRegion('Central Texas');
  }, [selectedRegion, setSelectedRegion]);

  // Detect device type and set default input mode
  useEffect(() => {
    const isMobileDevice = () => {
      // Check for touch support
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Check user agent for mobile patterns
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

      // Check screen size (optional)
      const isSmallScreen = window.innerWidth <= 768;

      return hasTouch && (isMobileUA || isSmallScreen);
    };

    // Set default input mode based on device type
    if (!isMobileDevice()) {
      setInputMode('upload'); // Desktop users likely want to upload
    }
    // Mobile devices default to 'camera' (already set in useState)
  }, []);

  // Calculate time of year and time of day based on system time
  useEffect(() => {
    const now = new Date();
    const month = now.getMonth(); // 0-11
    const hour = now.getHours(); // 0-23

    // Determine time of year
    let season = '';
    if (month >= 2 && month <= 4) season = 'Spring';
    else if (month >= 5 && month <= 7) season = 'Summer';
    else if (month >= 8 && month <= 10) season = 'Fall';
    else season = 'Winter';

    // Determine time of day
    let timePeriod = '';
    if (hour >= 5 && hour < 12) timePeriod = 'Morning';
    else if (hour >= 12 && hour < 17) timePeriod = 'Afternoon';
    else if (hour >= 17 && hour < 21) timePeriod = 'Evening';
    else timePeriod = 'Night';

    setTimeOfYear(season);
    setTimeOfDay(timePeriod);
  }, []);

  const triggerFileInput = () => {
    const fileInput = document.querySelector('.file-input') as HTMLInputElement;
    if (fileInput) fileInput.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const compressedFile = await compressImage(file, 800, 0.7);
        setSelectedFile(compressedFile);
        setError(null);

        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target?.result as string);
        reader.readAsDataURL(compressedFile);
      } catch (err) {
        console.error('Compression failed', err);
        setSelectedFile(file); // Fallback
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target?.result as string);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleIdentify = async () => {
    if (!selectedFile) return;
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const isPlant = await plantAnalysisService.checkPlant(selectedFile);
      if (!isPlant) {
        setError("We couldn't detect a plant. Please try a clearer photo.");
        setIsAnalyzing(false);
        return;
      }
      startAnalysis(selectedFile, selectedRegion, imagePreview || undefined);
    } catch (err) {
      console.error("Check failed, proceeding anyway", err);
      startAnalysis(selectedFile, selectedRegion, imagePreview || undefined);
    }
  };

  const clearImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setError(null);
  };

  return (
    <section className="upload-section">
      <div className="container">
        
        {/* Header */}
        <div className="upload-page-header">
          <h1 className="page-title">Identify Plant</h1>
          <p className="subtitle">
            Scan or upload a photo to detect invasive species.
          </p>
        </div>


        {/* Main Interface */}
        <div className="upload-interface">
          
          {/* File Input (Hidden) */}
          <input
            type="file"
            className="file-input"
            accept="image/*"
            capture={inputMode === 'camera' ? 'environment' : undefined}
            onChange={handleFileSelect}
          />

          {!imagePreview ? (
            <>
              {/* Modern Segmented Control Toggle */}
              <div className="mode-toggle-container">
                <div className="mode-toggle">
                  <button 
                    className={`toggle-btn ${inputMode === 'camera' ? 'active' : ''}`}
                    onClick={() => setInputMode('camera')}
                  >
                    <IconCamera className="toggle-icon" />
                    <span>Camera</span>
                  </button>
                  <button 
                    className={`toggle-btn ${inputMode === 'upload' ? 'active' : ''}`}
                    onClick={() => setInputMode('upload')}
                  >
                    <IconImage className="toggle-icon" />
                    <span>Upload</span>
                  </button>
                  <div className={`toggle-slider ${inputMode}`} />
                </div>
              </div>

              {/* Big Action Card */}
              <div 
                className="action-card" 
                onClick={triggerFileInput}
                role="button"
                tabIndex={0}
              >
                <div className="action-visual">
                  <div className="upload-graphic">
                    {inputMode === 'camera' ? (
                      <IconCamera size={48} />
                    ) : (
                      <IconUploadCloud size={48} />
                    )}
                  </div>
                </div>
                <h3 className="action-title">
                  {inputMode === 'camera' ? 'Tap to Take Photo' : 'Select Image from Gallery'}
                </h3>
                <p className="action-desc">
                  {inputMode === 'camera' 
                    ? 'Ensure the plant is centered and well-lit' 
                    : 'Supports JPG and PNG up to 10MB'}
                </p>
              </div>
            </>
          ) : (
            /* Image Preview Mode */
            <div className="preview-card">
              <div className="preview-image-wrapper">
                <img src={imagePreview} alt="Preview" className="preview-image" />
                <button className="remove-image-btn" onClick={clearImage} aria-label="Remove image">
                  <IconX size={18} />
                </button>
              </div>
              
              {error && <div className="error-banner">{error}</div>}

              <div className="preview-actions">
                <button 
                  className="button primary-button full-width"
                  onClick={handleIdentify}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? (
                    <span className="loading-dots">Analyzing...</span>
                  ) : (
                    <>Identify Plant</>
                  )}
                </button>
                <button className="button text-button" onClick={clearImage}>
                  Retake / Reselect
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Time Display */}
        <div className="time-card">
          <div className="time-row">
            <div className="time-icon-box">
              <IconCalendar size={18} />
            </div>
            <div className="time-content">
              <label>Time of Year</label>
              <div className="time-value">{timeOfYear}</div>
            </div>
          </div>
          <div className="time-row">
            <div className="time-icon-box">
              <IconClock size={18} />
            </div>
            <div className="time-content">
              <label>Time of Day</label>
              <div className="time-value">{timeOfDay}</div>
            </div>
          </div>
        </div>

        {/* Region Selector */}
        <div className="region-card">
          <div className="region-icon-box">
            <IconGlobe size={20} />
          </div>
          <div className="region-content">
            <label htmlFor="region-select">Texas Region</label>
            <div className="select-wrapper">
              <select
                id="region-select"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
              >
                {texasRegions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tips / Guidelines */}
        {!imagePreview && (
          <div className="tips-grid">
            <div className="tip-item">
              <span className="tip-badge">1</span>
              <span>Get Close</span>
            </div>
            <div className="tip-item">
              <span className="tip-badge">2</span>
              <span>Good Light</span>
            </div>
            <div className="tip-item">
              <span className="tip-badge">3</span>
              <span>Clear Focus</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default UploadPage;